import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import Icon from '@mui/material/Icon';

import DialogBase from './DialogBase';
import { InfraPlan } from '../../../models/plan/plan';
import { plannenService } from '../../../services/plannen.service';
import { ListParams } from '../../../models/listParams';

const PAGE_LIMIT = 20;
const SEARCH_DEBOUNCE_MS = 300;

const DialogPortfolioAddPlans: React.FC<any> = ({
  open,
  portfolio,
  preselectedPlanId,
  onClose,
  onSubmit,
}) => {

  const [plannen, setPlannen] = useState<InfraPlan[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<InfraPlan | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [popupOpen, setPopupOpen] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  // Tracks the most recent request so stale (out-of-order) responses are ignored.
  const requestIdRef = useRef(0);
  // Current offset for pagination, kept in a ref so it never lags behind async state.
  const offsetRef = useRef(0);
  // Prevents concurrent pagination fetches triggered by scroll events.
  const isFetchingRef = useRef(false);

  const fetchPlannen = useCallback(
    async (searchText: string, nextOffset: number) => {
      const requestId = ++requestIdRef.current;
      isFetchingRef.current = true;
      setLoading(true);

      try {
        const params: ListParams = {
          searchText,
          offset: nextOffset,
          limit: PAGE_LIMIT,
          sortBy: 'PlanNaam asc',
        };

        const data = await plannenService.getPlannen(params);

        // Ignore this response if a newer request has started in the meantime.
        if (requestId !== requestIdRef.current) {
          return;
        }

        const fetched: InfraPlan[] = data?.items ?? [];

        setPlannen((prev) => {
          const combined = nextOffset === 0 ? fetched : [...prev, ...fetched];

          // Dedupe by infplId as a safety net against overlapping pages.
          const seen = new Set<string>();
          return combined.filter((plan) => {
            const key = plan.infplId ?? '';
            if (seen.has(key)) {
              return false;
            }
            seen.add(key);
            return true;
          });
        });

        setHasMore(fetched.length === PAGE_LIMIT);
        offsetRef.current = nextOffset + PAGE_LIMIT;
      }
      catch (e) {
        if (requestId === requestIdRef.current) {
          console.error(e);
        }
      }
      finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
        }
        isFetchingRef.current = false;
      }
    },
    []
  );

  // Reset all state whenever the dialog is (re)opened.
  useEffect(() => {
    if (!open) return;

    requestIdRef.current += 1;
    offsetRef.current = 0;
    isFetchingRef.current = false;
    setInputValue('');
    setSelectedPlan(null);
    setPlannen([]);
    setHasMore(true);
    setLoading(true);
  }, [open]);

  // Preselect a plan (e.g. opened from the map popup) when the dialog opens.
  useEffect(() => {
    if (!open || !preselectedPlanId) return;

    let cancelled = false;

    (async () => {
      try {
        const plan: InfraPlan = await plannenService.getPlan(preselectedPlanId);
        if (cancelled || !plan) return;

        setPlannen((prev) => {
          const exists = prev.some((p) => p.infplId === plan.infplId);
          return exists ? prev : [plan, ...prev];
        });
        setSelectedPlan(plan);
        setInputValue(plan.planNaam || '');
      }
      catch (e) {
        if (!cancelled) console.error(e);
      }
    })();

    return () => { cancelled = true; };
  }, [open, preselectedPlanId]);

  // Debounced search: refetch from the first page whenever the search text changes.
  useEffect(() => {
    if (!open) return;

    const timer = setTimeout(() => {
      fetchPlannen(inputValue, 0);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [inputValue, open, fetchPlannen]);

  const onInputChange = (
    _event: React.SyntheticEvent,
    newValue: string,
    reason: string
  ) => {
    if (reason === 'input' || reason === 'clear' || reason === 'selectOption') {
      // Reset pagination synchronously so stale scroll events can't append
      // results from the previous search term.
      requestIdRef.current += 1;
      offsetRef.current = 0;
      isFetchingRef.current = false;
      setPlannen([]);
      setHasMore(true);
      // Show the loading state right away (during the debounce window) so the
      // "no options" message never flashes before the fetch starts.
      setLoading(true);
      setInputValue(newValue);
    }
  };

  const handleScroll = (event: any) => {
    const listboxNode = event.currentTarget;

    const bottomReached =
      listboxNode.scrollTop + listboxNode.clientHeight >=
      listboxNode.scrollHeight - 1;

    if (bottomReached && hasMore && !isFetchingRef.current) {
      fetchPlannen(inputValue, offsetRef.current);
    }
  };

  const handleSubmit = () => { 
    if (!selectedPlan) return;

    onSubmit(selectedPlan?.infplId); 

    setInputValue('');
    setSelectedPlan(null);
    onClose();
  };

  return (
    <DialogBase
      open={open}
      title={`Plannen uitnodigen voor ${portfolio?.name}`}
      contentText="U gaat een plan uitnodigen voor het afstemmingsdossier."
      onClose={onClose}
      onConfirm={handleSubmit}
      confirmButtonText="Verzenden"
      confirmButtonIcon={<Icon>send</Icon>}
    >
      <Autocomplete
        options={plannen}
        open={popupOpen}
        onOpen={() => setPopupOpen(true)}
        onClose={() => setPopupOpen(false)}
        getOptionLabel={(plan) => plan.planNaam || ''}
        isOptionEqualToValue={(option, value) => option.infplId === value.infplId}
        filterOptions={(x) => x}
        noOptionsText="Geen plannen gevonden"
        loadingText="Laden..."
        onInputChange={onInputChange}
        inputValue={inputValue}
        value={selectedPlan}
        onChange={(_e, value) => {
          setSelectedPlan(value);
          setInputValue(value?.planNaam || '');
        }}
        loading={loading}
        renderOption={(props, option) => (
          <li {...props}>
            {option.planNaam || 'Onbekend plan'} - {option.infplOrganizationName || 'Onbekende organisatie'}
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Plannen zoeken"
            variant="filled"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading && <CircularProgress size={20} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        ListboxProps={{ onScroll: handleScroll }}
      />
    </DialogBase>
  );
};

export default DialogPortfolioAddPlans;
